import { InvestorDetailsGetInvestorLegalEntitiesQuery } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Accordion, AccordionActionButtonItem } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { Tooltip } from '@/components/basicComponents/tooltip';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { EntityGroupingZeroState } from '../../components';
import DeleteGroup from '../../components/modals/deleteGroup';
import {
  EditInvestorModalPageWrapper,
  EditInvestorPreviewFieldItem,
  EditInvestorPreviewFields,
  EditInvestorPreviewFieldTitle,
  EditInvestorPreviewFieldValue,
  EditInvestorSectionsWrapper
} from '../../styles';
import { EditGroupModalPage } from './editGroupModalPage';

const hasEnoughUngroupedEntities = (investorLegalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities']) => {
  const ungroupedEntities = investorLegalEntities.filter((entity) => !entity.isGroup);
  return ungroupedEntities.length >= 2;
};

interface EntityGroupingModalPageProps {
  groups: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
  investorLegalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
  investorLegalEntitiesRefetch: () => Promise<ApolloQueryResult<InvestorDetailsGetInvestorLegalEntitiesQuery>>;
  handleCloseModalPage: () => void;
}

export const EntityGroupingModalPage = ({
  groups,
  investorLegalEntities,
  investorLegalEntitiesRefetch,
  handleCloseModalPage
}: EntityGroupingModalPageProps) => {
  const { isMobile } = useResponsive();

  const [currentGroup, setCurrentGroup] = useState<InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'][number] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [isEditGroupModalPageOpen, setIsEditGroupModalPageOpen] = useState(false);

  const handleEditGroupModalPage = ({
    open,
    group
  }: {
    open: boolean;
    group?: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'][number];
  }) => {
    setIsEditGroupModalPageOpen(open);
    setCurrentGroup(group || null);
  };

  const handleModalWindow = ({
    type,
    group
  }: {
    type: string;
    group?: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'][number];
  }) => {
    setModalWindow({ type, isOpen: !!type });
    setCurrentGroup(group || null);
  };

  const canCreateGroup = hasEnoughUngroupedEntities(investorLegalEntities);

  if (isEditGroupModalPageOpen) {
    return (
      <EditGroupModalPage
        currentGroup={currentGroup}
        investorLegalEntities={investorLegalEntities}
        investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
        handleCloseModalPage={() => handleEditGroupModalPage({ open: false })}
      />
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-group' && currentGroup && (
        <DeleteGroup
          isOpen={modalWindow.isOpen}
          currentGroup={currentGroup}
          investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
          onClose={() => handleModalWindow({ type: '' })}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Edit Investor" />} />
        <PageTitle title="Entity Grouping" />
        <EditInvestorModalPageWrapper>
          {!groups?.length ? (
            <EntityGroupingZeroState canCreateGroup={canCreateGroup} openEditGroupModalPage={() => handleEditGroupModalPage({ open: true })} />
          ) : (
            <EntityGroupingWrapper>
              <AddGroupWrapper isMobile={isMobile}>
                <Tooltip
                  maxContentWidth={500}
                  title={
                    canCreateGroup
                      ? ''
                      : 'Groups require two or more entities. Either add additional entities to this household or ungroup entities before adding a new group.'
                  }
                >
                  <Button
                    size="md"
                    onClick={() => handleEditGroupModalPage({ open: true })}
                    styleType={canCreateGroup ? 'default' : 'disabled'}
                    icon={<PlusIcon width={20} height={20} fill={themeVar('context.light')} />}
                  >
                    Add a Group
                  </Button>
                </Tooltip>

                <Banner
                  title=""
                  description="By default each entity within a household is managed as a separate portfolio.  Group two or more entities to treat them as a single portfolio for managing capacity."
                  icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
                  bgColor={themeVar('layer.base')}
                  color={themeVar('font.base')}
                  dividerColor={themeVar('border.base')}
                />
              </AddGroupWrapper>
              <EditInvestorSectionsWrapper>
                {groups.map((group) => (
                  <Accordion
                    key={group.id}
                    title={group.entityName}
                    collapsable
                    onEdit={() => handleEditGroupModalPage({ open: true, group })}
                    customActionButtons={
                      <AccordionActionButtonItem
                        id="accordionActionButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleModalWindow({ type: 'delete-group', group });
                        }}
                        isMobile={isMobile}
                      >
                        <DeleteBucketIcon width={17} height={22} fill={themeVar('font.action')} />
                      </AccordionActionButtonItem>
                    }
                  >
                    <EditInvestorPreviewFields>
                      <EditInvestorPreviewFieldItem isMobile={isMobile}>
                        <EditInvestorPreviewFieldTitle>Default Entity</EditInvestorPreviewFieldTitle>
                        <EditInvestorPreviewFieldValue>
                          {group?.entities?.find((entity) => entity.primaryEntity)?.entityName || ''}
                        </EditInvestorPreviewFieldValue>
                      </EditInvestorPreviewFieldItem>
                      <EditInvestorPreviewFieldItem isMobile={isMobile}>
                        <EditInvestorPreviewFieldTitle>Additional Grouped Entities</EditInvestorPreviewFieldTitle>
                        <EditInvestorPreviewFieldValue>
                          {group?.entities?.filter((entity) => !entity.primaryEntity)?.map((entity) => <p key={entity.id}>{entity.entityName}</p>)}
                        </EditInvestorPreviewFieldValue>
                      </EditInvestorPreviewFieldItem>
                    </EditInvestorPreviewFields>
                  </Accordion>
                ))}
              </EditInvestorSectionsWrapper>
            </EntityGroupingWrapper>
          )}
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const EntityGroupingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AddGroupWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 30px;
  padding: 10px 20px;
`;
